import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Document } from '@/features/docs/Document'
import { type DocumentStore } from '@/features/docs/DocumentStore'
import { fromAbortSignal } from '@/utils/rx-util'
import { takeUntil, tap } from 'rxjs/operators'
import { required, validationContext } from 'validx'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'

export class RenameDocumentDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor newName = ''
  @observable accessor document: Document | null = null

  readonly validation = validationContext(this, {
    newName: [required('Name is required')],
  })

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _documentStore: DocumentStore,
  ) {}

  @action.bound
  show(document: Document, signal?: AbortSignal) {
    // Close on abort
    fromAbortSignal(signal)
      .pipe(tap(this.cancel), takeUntil(this.modal.close$))
      .subscribe()

    this.document = document
    this.newName = document.title
    this.modal.show()
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  rename = task.resolved(async () => {
    if (!this.document || !this.validation.reset().validate().isValid) {
      return
    }

    await this._documentStore
      .rename(this.document, this.newName)
      .catch(this._createSnackbarErrorHandler('Renaming failed'))
    this.modal.close()
  })
}
